import React, { useState, useEffect } from "react";
import { calcAgeYears } from "../../lib/age";

const PersonalInfoQuestion = ({ onSubmit, onNext, initialValue, onInfantBlock }) => {
  const [name, setName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dateError, setDateError] = useState("");
  const [nameError, setNameError] = useState("");
  const nameInputRef = React.useRef(null);

  useEffect(() => {
    if (initialValue) {
      setName(initialValue.name || "");
      setDateOfBirth(initialValue.dateOfBirth || "");
    }
  }, [initialValue]);

  // Delayed autofocus to allow smooth transition
  useEffect(() => {
    const timer = setTimeout(() => {
      nameInputRef.current?.focus();
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Function to validate name (must have both first and last name)
  const validateName = (nameValue) => {
    if (!nameValue || nameValue.trim() === "") {
      return "This field is required.";
    }
    if (!/.+ .+/.test(nameValue.trim())) {
      return "Please enter both first and last name.";
    }
    return ""; // No error
  };

  // Function to validate date of birth
  const validateDateOfBirth = (dateString) => {
    // Check if date format is complete (MM/DD/YYYY)
    if (dateString.length < 10) {
      return "Please enter a complete date";
    }

    // Parse the date
    const [month, day, year] = dateString.split('/').map(Number);

    // Check if month is valid (1-12)
    if (month < 1 || month > 12) {
      return "Month must be between 1 and 12";
    }

    // Check if day is valid (1-31)
    if (day < 1 || day > 31) {
      return "Day must be between 1 and 31";
    }

    // Check if year is reasonable (not in the future, not more than 120 years ago)
    const currentYear = new Date().getFullYear();
    if (year > currentYear) {
      return "Date of birth cannot be in the future";
    }

    if (year < currentYear - 120) {
      return "Date of birth cannot be more than 120 years ago";
    }

    // Additional validation for specific month/day combinations
    const date = new Date(year, month - 1, day);
    if (date.getMonth() !== month - 1 || date.getDate() !== day) {
      return "Invalid date (e.g., February 30th doesn't exist)";
    }

    return ""; // No error
  };

  // Function to automatically format date input with slashes
  const formatDateInput = (inputValue) => {
    // Remove all non-numeric characters
    const numbers = inputValue.replace(/\D/g, '');
    
    // Limit to 8 digits
    const limitedNumbers = numbers.slice(0, 8);
    
    // Format as MM/DD/YYYY
    if (limitedNumbers.length <= 2) {
      return limitedNumbers;
    } else if (limitedNumbers.length <= 4) {
      return `${limitedNumbers.slice(0, 2)}/${limitedNumbers.slice(2)}`;
    } else {
      return `${limitedNumbers.slice(0, 2)}/${limitedNumbers.slice(2, 4)}/${limitedNumbers.slice(4)}`;
    }
  };

  const handleDateChange = (e) => {
    const inputValue = e.target.value;
    const formatted = formatDateInput(inputValue);
    setDateOfBirth(formatted);
    
    // Clear error when user starts typing
    if (dateError) {
      setDateError("");
    }

    // Validate when date is complete
    if (formatted.length === 10) {
      const error = validateDateOfBirth(formatted);
      setDateError(error);
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const finalNameError = validateName(name);
    const finalDateError = validateDateOfBirth(dateOfBirth);
    
    if (finalNameError) {
      setNameError(finalNameError);
    }
    if (finalDateError) {
      setDateError(finalDateError);
    }
    
    if (finalNameError || finalDateError) {
      return;
    }

    const answer = { name, dateOfBirth };
    // Always store the answer
    onSubmit(answer);

    // Age gate: if <= 2, redirect to Infant flow/page instead of continuing
    const age = calcAgeYears(dateOfBirth);
    if (!Number.isNaN(age) && age <= 2 && typeof onInfantBlock === "function") {
      onSubmit({ name, dateOfBirth }); // still store the answer if you want
      onInfantBlock();
      return;
    }

    // Normal flow
    onNext();

    // Reset fields
    setName("");
    setDateOfBirth("");
    setDateError("");
    setNameError("");
  };

  const canSubmit = name.trim() !== "" && dateOfBirth.trim() !== "" && !dateError && !nameError;

  return (
    <form onSubmit={handleSubmit} className="input-form">
      <div className="dual-input-container">
        <div className="input-group">
          <label htmlFor="name" className="input-label">First and Last Name</label>
          <input
            id="name"
            type="text"
            className={`input-field ${nameError ? 'input-error' : ''}`}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              // Clear error when user starts typing
              if (nameError) {
                setNameError("");
              }
            }}
            ref={nameInputRef}
            placeholder="John Smith"
          />
          {nameError && (
            <div className="error-message">{nameError}</div>
          )}
        </div>
        <div className="input-group">
          <label htmlFor="dob" className="input-label">Date of Birth</label>
          <input
            id="dob"
            type="tel"
            className={`input-field ${dateError ? 'input-error' : ''}`}
            value={dateOfBirth}
            onChange={handleDateChange}
            placeholder="MM/DD/YYYY"
            maxLength="10"
          />
          {dateError && (
            <div className="error-message">{dateError}</div>
          )}
        </div>
        <button
          type="submit"
          className="send-button dual-input-button"
          disabled={!canSubmit}
        >
          Continue
        </button>
      </div>
    </form>
  );
};

export default PersonalInfoQuestion; 